import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import { includes, has, propOr } from "ramda";
import { styled } from "@mui/material/styles";
import { ResizableBox } from "react-resizable";

import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import Draggable from "react-draggable";

import ShowMaterialModal from "~/components/Modal/ShowMaterialModal";
import EditMaterialModal from "~/components/Modal/EditMaterialModal";
import NewMaterialModal from "~/components/Modal/NewMaterialModal";
import PrintOptionsModal from "~/components/Modal/PrintOptionsModal";
import DeleteMaterialPositionModal from "~/components/Modal/DeleteMaterialPositionModal";
import PouchErrorModal from "~/components/Modal/PouchErrorModal";
import ViewerModal from "~/components/Modal/ViewerModal";
import FrameModal from "~/components/Modal/FrameModal";
import MaterialRendererModal from "~/components/Modal/MaterialRendererModal";
import MaterialInfoModal from "~/components/Modal/MaterialInfoModal";
import NotesModal from "~/components/Modal/NotesModal";
import SessionRevokedModal from "~/components/Modal/SessionRevokedModal";
import SessionInvalidModal from "~/components/Modal/SessionInvalidModal";
import UsersettingsModal from "~/components/Modal/UsersettingsModal";
// import DeleteAllAnnotationsModal from "~/components/AnnotationeerControls/DeleteAllAnnotationsModal";

import DlsUserEditMaterialModal from "~/components/Modal/DlsUserMaterial/EditModal";
import DlsUserNewMaterialModal from "~/components/Modal/DlsUserMaterial/NewModal";
import DlsUserShowMaterialModal from "~/components/Modal/DlsUserMaterial/ShowModal";

import PrintAllPagesStaticModal from "~/components/Modal/PrintAllPagesStaticModal";

const MODAL_COMPONENTS = {
  SHOW_MATERIAL: ShowMaterialModal,
  EDIT_MATERIAL: EditMaterialModal, // Edit mode
  NEW_MATERIAL: NewMaterialModal, // Edit mode
  PRINT_OPTIONS: PrintOptionsModal, //
  DELETE_MATERIAL_POSITION: DeleteMaterialPositionModal, //
  POUCH_ERROR: PouchErrorModal, //
  VIEWER: ViewerModal, //
  MATERIAL_RENDERER: MaterialRendererModal, //
  IFRAME: FrameModal, //
  MATERIAL_INFO: MaterialInfoModal, //
  NOTES: NotesModal, //
  DLS_USER_MATERIAL_EDIT: DlsUserEditMaterialModal, //
  DLS_USER_MATERIAL_NEW: DlsUserNewMaterialModal, //
  DLS_USER_MATERIAL_SHOW: DlsUserShowMaterialModal, //
  PRINT_ALL_STATIC: PrintAllPagesStaticModal, // kommt nie vor?
  SESSION_REVOKED_MODAL: SessionRevokedModal, //
  SESSION_INVALID_MODAL: SessionInvalidModal, //
  USERSETTINGS: UsersettingsModal //
  // DELETE_ALL_ANNOTATIONS: DeleteAllAnnotationsModal //
};

const Resizable = styled(ResizableBox)`
  display: flex;
  flex-direction: column;
  & .react-resizable-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    bottom: 0;
    right: 0;
    padding: 0 3px 3px 0;
    background: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+")
      no-repeat bottom right;
    background-origin: content-box;
    box-sizing: border-box;
    cursor: se-resize;
  }
`;

// TODO jss-to-tss-react codemod: Unable to handle style definition reliably. ArrowFunctionExpression in CSS prop.
const BaseDialog = styled(Dialog)(props => {
  const zIndex = props.kind === "PRINT_OPTIONS" ? { zIndex: "5501 !important" } : {};
  return {
    "&.MuiDialog-root": {
      pointerEvents: props.hideBackdrop ? "none" : "auto",
      /* 1500 is inline, daher das important
       print option werden <5500 von quickdial überlagert und button ist auf Tablet nicht mehr klickbar.
       Ich wollte es nicht global überschreiben wegen Nebeneffekten
       */
      ...zIndex,
      "@media print": {
        display: "none"
      }
    },
    "&.MuiDialog-root [class*='MuiPaper-root']": {
      minWidth: props.theme.typography.pxToRem(420),
      overflow: props.isMinimised ? "hidden" : "initial",
      pointerEvents: "auto"
    },
    "&.MuiDialog-root [class*='MuiDialogContent-root']": {
      display: props.isMinimised ? "none" : "initial",
      padding: props.fullScreen ? props.theme.spacing(0) : props.theme.spacing(0, 2)
    },
    "&.MuiDialog-root [class*='MuiDialogActions-root']": {
      display: props.isMinimised ? "none" : "flex"
    },
    "& .MuiDialog-scrollPaper": {
      padding: props.theme.spacing(3)
    }
  };
});

const directions = ["up", "right", "down", "left"];

const SlideTransition = forwardRef(({ direction, ...props }, ref) => (
  <Slide direction={direction} ref={ref} {...props} />
));

SlideTransition.displayName = "SlideTransition";

SlideTransition.propTypes = {
  direction: PropTypes.string
};

const FadeTransition = forwardRef(({ direction, ...props }, ref) => <Fade ref={ref} {...props} />);

FadeTransition.displayName = "FadeTransition";

FadeTransition.propTypes = {
  direction: PropTypes.string
};

function DraggablePaper(props) {
  return (
    <Draggable
      handle={'[class*="MuiDialogTitle-root"]'}
      cancel={'[class*="MuiDialogContent-root"], button'}
      bounds="parent"
    >
      <Paper {...props} />
    </Draggable>
  );
}

const StyledPaper = styled(Paper)`
  display: flex;
  position: absolute;
  z-index: 6000;
  flex-direction: column;
  box-shadow: 3px 3px 6px 6px rgba(0, 0, 0, 0.5);
`;

function ModalPortal({
  kind,
  props,
  isMaximised,
  isMinimised,
  hideModal,
  showModal,
  lastX,
  lastY,
  setLastX,
  setLastY,
  lastWidth,
  lastHeight,
  setLastWidth,
  setLastHeight
}) {
  const Modal = MODAL_COMPONENTS[kind];
  console.log("kind", kind);
  const isFullScreen = propOr(false, "fullScreen", props);
  const direction = propOr("", "transition", props);
  const isDraggable = propOr(true, "draggable", props);
  const PaperComponent = isDraggable ? DraggablePaper : Paper;
  let TransitionComponent = FadeTransition;

  if (includes(direction, directions)) {
    TransitionComponent = SlideTransition;
  }

  const materialDisplayType = props?.renderType ? props.renderType : props?.materialType;
  const isFixedModal = ["PRINT_OPTIONS", "DELETE_MATERIAL_POSITION", "POUCH_ERROR"].includes(kind);
  const isNonClosable = ["SESSION_REVOKED_MODAL", "SESSION_INVALID_MODAL", "POUCH_ERROR"].includes(kind);
  const isAudioPlayer = ["directplayer", "audio"].includes(materialDisplayType);
  const isNotesModal = kind === "NOTES";

  const hideBackdrop = useMemo(
    () => (isMinimised && !isFixedModal) || isAudioPlayer,
    [isMinimised, isFixedModal, isAudioPlayer]
  );

  if (!has(kind, MODAL_COMPONENTS)) {
    return null;
  }

  function getTransitionProps() {
    return { direction };
  }

  function getInitialResizableWidth() {
    if (lastWidth) return lastWidth;

    const maxWidth = 600;
    return window.innerWidth > maxWidth ? maxWidth : window.innerWidth;
  }

  function getInitialResizableHeight() {
    return lastHeight || 375;
  }

  function getNotesModalDefaultPosition() {
    if (lastX && lastY) {
      return { x: lastX, y: lastY };
    }
    const initialWidth = getInitialResizableWidth();
    return {
      x: window.innerWidth - window.innerWidth / 2 - initialWidth / 2,
      y: -(window.innerHeight - window.innerHeight / 2 + 211)
    };
  }

  const onDraggableStop = (_, data) => {
    setLastX(data.x);
    setLastY(data.y);
  };

  const onResizableStop = (_, data) => {
    setLastWidth(data.size.width);
    setLastHeight(data.size.height);
  };

  if (isNotesModal) {
    return (
      <Draggable
        bounds="parent"
        defaultPosition={getNotesModalDefaultPosition()}
        cancel={'[class*="MuiDialogContent-root"]'}
        handle=".notes-dialog-title"
        onStop={onDraggableStop}
        disabled
      >
        <StyledPaper>
          <Resizable
            height={getInitialResizableHeight()}
            width={getInitialResizableWidth()}
            minConstraints={[300, 300]}
            maxConstraints={[800, 600]}
            onResizeStop={onResizableStop}
            aria-labelledby={`${kind}-modal-title`}
          >
            <Modal
              {...props}
              kind={kind}
              hideModal={hideModal}
              showModal={showModal}
              isMaximised={isFixedModal ? true : isMaximised}
              isMinimised={isFixedModal ? false : isMinimised}
            />
          </Resizable>
        </StyledPaper>
      </Draggable>
    );
  }

  return (
    <BaseDialog
      open
      fullScreen={isMinimised ? false : isFullScreen}
      hideBackdrop={hideBackdrop}
      disableEnforceFocus={isAudioPlayer}
      isMaximised={isMaximised}
      isMinimised={isMinimised}
      PaperComponent={PaperComponent}
      kind={kind}
      slots={{
        transition: TransitionComponent
      }}
      slotProps={{
        transition: getTransitionProps()
      }}
      onClose={event => {
        if (isNonClosable) {
          return;
        }
        if (isAudioPlayer && event.key !== "Escape") {
          return;
        }

        hideModal();
      }}
      disableEscapeKeyDown={isNonClosable}
      aria-labelledby={`${kind}-modal-title`}
    >
      <Modal
        {...props}
        kind={kind}
        hideModal={hideModal}
        showModal={showModal}
        isMaximised={isFixedModal ? true : isMaximised}
        isMinimised={isFixedModal ? false : isMinimised}
      />
    </BaseDialog>
  );
}

ModalPortal.propTypes = {
  kind: PropTypes.string,
  props: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isMaximised: PropTypes.bool.isRequired,
  isMinimised: PropTypes.bool.isRequired,
  itemType: PropTypes.string,
  hideModal: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  lastX: PropTypes.number,
  lastY: PropTypes.number,
  setLastX: PropTypes.func.isRequired,
  setLastY: PropTypes.func.isRequired,
  lastWidth: PropTypes.number,
  lastHeight: PropTypes.number,
  setLastWidth: PropTypes.func.isRequired,
  setLastHeight: PropTypes.func.isRequired
};

export default ModalPortal;
